'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useCallback, useState } from 'react';
interface UseInactivityTimeoutOptions {
  timeoutMs: number;
  warningMs: number;
  warningEnabled: boolean;
  onTimeout: () => void;
  onWarn?: () => void;
  onActivity?: () => void;
}
interface UseInactivityTimeoutResult {
  isWarning: boolean;
  secondsRemaining: number;
  resetTimer: () => void;
}
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;
const CHANNEL_NAME = 'penny-inactivity';
type ChannelMessage = {
  type: 'activity';
  at: number;
} | {
  type: 'reset';
};
export function useInactivityTimeout(t0) {
  const $ = _c(26);
  const {
    timeoutMs,
    warningMs,
    warningEnabled,
    onTimeout,
    onWarn,
    onActivity
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Date.now();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const lastActivityAt = useRef(t1);
  const onTimeoutRef = useRef(onTimeout);
  const onWarnRef = useRef(onWarn);
  const onActivityRef = useRef(onActivity);
  const warnFired = useRef(false);
  const channelRef = useRef(null);
  const [isWarning, setIsWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(timeoutMs / 1000));
  let t2;
  let t3;
  if ($[1] !== onTimeout) {
    t2 = () => {
      onTimeoutRef.current = onTimeout;
    };
    t3 = [onTimeout];
    $[1] = onTimeout;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== onWarn) {
    t4 = () => {
      onWarnRef.current = onWarn;
    };
    t5 = [onWarn];
    $[4] = onWarn;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== onActivity) {
    t6 = () => {
      onActivityRef.current = onActivity;
    };
    t7 = [onActivity];
    $[7] = onActivity;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] !== timeoutMs) {
    t8 = () => {
      lastActivityAt.current = Date.now();
      warnFired.current = false;
      setIsWarning(false);
      setSecondsRemaining(Math.ceil(timeoutMs / 1000));
      channelRef.current?.postMessage({
        type: "reset"
      } as ChannelMessage);
    };
    $[10] = timeoutMs;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const resetTimer = t8;
  let t9;
  if ($[12] !== timeoutMs) {
    t9 = () => {
      if (typeof BroadcastChannel === "undefined") {
        return;
      }
      const ch = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = ch;
      ch.onmessage = event => {
        const msg = event.data;
        if (msg.type === "activity") {
          if (msg.at > lastActivityAt.current) {
            lastActivityAt.current = msg.at;
            if (warnFired.current) {
              warnFired.current = false;
              setIsWarning(false);
              setSecondsRemaining(Math.ceil(timeoutMs / 1000));
            }
          }
        } else {
          if (msg.type === "reset") {
            lastActivityAt.current = Date.now();
            warnFired.current = false;
            setIsWarning(false);
            setSecondsRemaining(Math.ceil(timeoutMs / 1000));
          }
        }
      };
      return () => {
        ch.close();
        channelRef.current = null;
      };
    };
    $[12] = timeoutMs;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const handleActivity = () => {
        if (warnFired.current) {
          return;
        }
        const now = Date.now();
        lastActivityAt.current = now;
        channelRef.current?.postMessage({
          type: "activity",
          at: now
        } as ChannelMessage);
        onActivityRef.current?.();
      };
      ACTIVITY_EVENTS.forEach(event_0 => document.addEventListener(event_0, handleActivity, {
        passive: true
      }));
      return () => {
        ACTIVITY_EVENTS.forEach(event_1 => document.removeEventListener(event_1, handleActivity));
      };
    };
    t12 = [];
    $[15] = t11;
    $[16] = t12;
  } else {
    t11 = $[15];
    t12 = $[16];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[17] !== timeoutMs || $[18] !== warningEnabled || $[19] !== warningMs) {
    t13 = () => {
      const interval = setInterval(() => {
        const elapsed = Date.now() - lastActivityAt.current;
        const remaining = Math.max(0, timeoutMs - elapsed);
        setSecondsRemaining(Math.ceil(remaining / 1000));
        if (elapsed >= timeoutMs) {
          clearInterval(interval);
          onTimeoutRef.current();
          return;
        }
        const effectiveWarningMs = Math.min(warningMs, Math.max(0, timeoutMs - 1000));
        if (warningEnabled && !warnFired.current && elapsed >= timeoutMs - effectiveWarningMs) {
          warnFired.current = true;
          setIsWarning(true);
          onWarnRef.current?.();
        }
      }, 1000);
      return () => clearInterval(interval);
    };
    t14 = [timeoutMs, warningMs, warningEnabled];
    $[17] = timeoutMs;
    $[18] = warningEnabled;
    $[19] = warningMs;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  useEffect(t13, t14);
  let t15;
  if ($[22] !== isWarning || $[23] !== resetTimer || $[24] !== secondsRemaining) {
    t15 = {
      isWarning,
      secondsRemaining,
      resetTimer
    };
    $[22] = isWarning;
    $[23] = resetTimer;
    $[24] = secondsRemaining;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  return t15;
}